import React, { useState } from "react";
import { GoogleMap, useJsApiLoader, MarkerF, CircleF } from "@react-google-maps/api";
import { X, MapPin, Swords, Store } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Muted basemap so the pins and their numbers are the loudest thing on screen.
const MAP_STYLES = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", elementType: "labels.text.fill", stylers: [{ color: "#9aa5b1" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#8a949f" }] },
  { featureType: "administrative", elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }, { weight: 3 }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f4f5f3" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dbe8ef" }] },
];

const pinSvg = (label, active) => {
  const body = active ? "#16a34a" : "#0f172a";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="54" height="66" viewBox="0 0 54 66">
    <defs><filter id="s" x="-40%" y="-40%" width="180%" height="180%">
      <feDropShadow dx="0" dy="2" stdDeviation="2.4" flood-color="#0f172a" flood-opacity="0.35"/>
    </filter></defs>
    <g filter="url(#s)">
      <path d="M27 63 L18.5 47 h17 Z" fill="#ffffff"/>
      <path d="M27 60 L20.5 47 h13 Z" fill="${body}"/>
      <rect x="3" y="6" width="48" height="42" rx="15" fill="#ffffff"/>
      <rect x="6" y="9" width="42" height="36" rx="12.5" fill="${body}"/>
      <text x="27" y="32.5" text-anchor="middle" font-family="Helvetica,Arial,sans-serif"
        font-size="17" font-weight="700" fill="#ffffff">${label}</text>
    </g>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const youSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="66" height="66" viewBox="0 0 66 66">
    <circle cx="33" cy="33" r="22" fill="#16a34a" fill-opacity="0.18"/>
    <circle cx="33" cy="33" r="13" fill="#ffffff"/>
    <circle cx="33" cy="33" r="10" fill="#16a34a"/>
    <circle cx="33" cy="33" r="4" fill="#ffffff"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const dist = (km, t) => km == null ? "—" : km === 0 ? t("an3.sameSpot")
  : km < 0.2 ? t("an3.underTenth") : t("an3.kmAway", { km });

export const RivalMap = ({ myLat, myLng, rivals = [], radiusKm, height = 320, onCompare }) => {
  const { t } = useLang();
  const { isLoaded } = useJsApiLoader({ id: "gmap-script", googleMapsApiKey: GKEY || "" });
  const [open, setOpen] = useState(null);
  const pins = rivals.filter((r) => r.lat != null && r.lng != null);

  if (!GKEY || myLat == null) return null;
  if (!isLoaded) return <div className="rounded-2xl bg-secondary animate-pulse" style={{ height }} />;

  const zoom = radiusKm ? (radiusKm <= 1 ? 14 : radiusKm <= 3 ? 13 : radiusKm <= 5 ? 12 : radiusKm <= 10 ? 11 : 9) : 10;
  const sel = open == null ? null : pins[open];

  return (
    <div className="relative rounded-2xl overflow-hidden border border-border shadow-[0_8px_30px_rgba(15,23,42,0.10)]"
      data-testid="rival-map" style={{ height }}>
      <div className="pointer-events-none absolute top-3 left-3 z-10 flex items-center gap-2.5 rounded-full
        bg-background/90 backdrop-blur-md border border-border px-3 py-1.5 text-[11px] font-bold text-muted-foreground shadow-sm">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#16a34a] ring-2 ring-[#16a34a]/25" />{t("an3.legendYou")}</span>
        <span className="h-3 w-px bg-border" />
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#0f172a]" />{t("an3.legendRival")}</span>
      </div>

      <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }}
        center={{ lat: myLat, lng: myLng }} zoom={zoom}
        onClick={() => setOpen(null)}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false,
          clickableIcons: false, styles: MAP_STYLES }}>
        {radiusKm ? (
          <CircleF center={{ lat: myLat, lng: myLng }} radius={radiusKm * 1000}
            options={{ strokeColor: "#16a34a", strokeOpacity: 0.4, strokeWeight: 1.5,
              fillColor: "#16a34a", fillOpacity: 0.06, clickable: false }} />
        ) : null}
        <MarkerF position={{ lat: myLat, lng: myLng }} title={t("an3.legendYou")} zIndex={50}
          icon={{ url: youSvg(), scaledSize: new window.google.maps.Size(66, 66),
            anchor: new window.google.maps.Point(33, 33) }} />
        {pins.map((r, i) => (
          <MarkerF key={`${r.business}-${i}`} position={{ lat: r.lat, lng: r.lng }}
            onClick={() => setOpen(i)} zIndex={open === i ? 99 : 60}
            title={r.business}
            icon={{ url: pinSvg(`${Math.round(r.avgDiscount)}%`, open === i),
              scaledSize: new window.google.maps.Size(54, 66),
              anchor: new window.google.maps.Point(27, 63) }} />
        ))}
      </GoogleMap>

      {sel && (
        <div className="absolute inset-x-3 bottom-8 z-20 animate-in fade-in slide-in-from-bottom-2 duration-200
          sm:inset-x-auto sm:left-3 sm:max-w-sm" data-testid={`rival-pin-info-${open}`}>
          <div className="rounded-2xl border border-white/10 backdrop-blur-xl p-4
            shadow-[0_18px_50px_rgba(2,6,23,0.55)] text-white"
            style={{ backgroundColor: "rgba(11,18,32,0.94)" }}>
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                <Store size={16} className="text-[#4ade80]" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold truncate" data-testid="rival-pin-name">{sel.business}</p>
                <p className="text-[11px] text-white/55 truncate">
                  {[sel.location, sel.country].filter((x, idx, arr) => x && arr.indexOf(x) === idx).join(", ")}
                </p>
              </div>
              <button onClick={() => setOpen(null)} data-testid="rival-pin-close"
                className="h-8 w-8 shrink-0 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <X size={14} />
              </button>
            </div>

            <div className="mt-3 flex items-stretch gap-2">
              <div className="rounded-xl bg-[#16a34a]/18 border border-[#16a34a]/30 px-3 py-2">
                <p className="text-lg font-extrabold leading-none text-[#4ade80]">{sel.avgDiscount}%</p>
                <p className="text-[10px] font-semibold text-white/55 mt-0.5">avg off</p>
              </div>
              <div className="rounded-xl bg-white/[0.06] border border-white/10 px-3 py-2 flex-1">
                <p className="text-[11px] font-bold text-white/85">{t("an3.liveCount", { n: sel.activeDeals })}</p>
                <p className="text-[11px] font-bold text-white/85 mt-1 flex items-center gap-1">
                  <MapPin size={11} className="text-[#4ade80]" /> {dist(sel.distanceKm, t)}
                  {sel.locations > 1 ? <span className="text-white/45 font-semibold">· {t("an3.locationsCount", { n: sel.locations })}</span> : null}
                </p>
              </div>
            </div>

            {onCompare && (
              <button onClick={() => onCompare(sel.business)} data-testid="rival-pin-compare"
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#16a34a]
                  hover:bg-[#15803d] text-white text-xs font-bold px-3 py-2.5 transition-colors">
                <Swords size={13} /> {t("an3.compareRival")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RivalMap;
